import { useState, useEffect } from 'react'
import { AlertTriangle, RotateCcw, Trash2, Zap, Bell, Loader2 } from 'lucide-react'
import api from '../../services/api'
import { useAuth, userRoles } from '../../hooks/useAuth'
import { useTranslation } from 'react-i18next'

// One row per reset action. Each shows: title, what it touches, an input (if needed), and a button.
export default function DemoReset() {
  const { t } = useTranslation('people')
  const { user } = useAuth()
  const [enabled, setEnabled] = useState(null) // null=loading, true/false
  const [consultants, setConsultants] = useState([])
  const [projects, setProjects] = useState([])
  const [contracts, setContracts] = useState([])
  const [targets, setTargets] = useState({ user_id: '', project_id: '', contract_id: '' })
  const [busy, setBusy] = useState({})       // per-action loading flags
  const [msg, setMsg] = useState(null)        // { type, text }
  const [confirming, setConfirming] = useState(null) // which action is awaiting confirmation

  useEffect(() => {
    api.get('/demo/status').then(r => setEnabled(!!r.data.demo_mode_enabled)).catch(() => setEnabled(false))
    api.get('/users/').then(r => setConsultants(r.data.filter(u => u.role === 'Consultant'))).catch(() => {})
    api.get('/projects/').then(r => setProjects(r.data)).catch(() => {})
    api.get('/contracts/').then(r => setContracts(r.data)).catch(() => {})
  }, [])

  const run = async (key, request, successText) => {
    setBusy(b => ({ ...b, [key]: true }))
    setMsg(null)
    try {
      const r = await request()
      setMsg({ type: 'success', text: r.data?.detail || successText })
      setConfirming(null)
      // refresh dependent lists in case anything was deleted
      api.get('/projects/').then(r => setProjects(r.data)).catch(() => {})
      api.get('/contracts/').then(r => setContracts(r.data)).catch(() => {})
    } catch (e) {
      setMsg({ type: 'error', text: e.response?.data?.detail || 'Reset failed' })
    } finally {
      setBusy(b => ({ ...b, [key]: false }))
    }
  }

  if (enabled === null) {
    return <div className="p-6 text-gray-500"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Checking demo mode…</div>
  }

  if (user?.role !== userRoles.ADMIN) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-xl p-5 text-red-700">
          🚫 Demo / Testing tools are Admin-only.
        </div>
      </div>
    )
  }

  if (!enabled) {
    return (
      <div className="p-6 max-w-2xl">
        <h1 className="text-2xl font-bold text-gray-800 mb-2">{t('demo_testing')}</h1>
        <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-5 text-sm text-amber-800">
          <p className="font-semibold mb-1">{t('demo_off')}</p>
          <p>Reset endpoints are disabled in this environment. To enable, set
            <code className="bg-amber-100 px-1.5 py-0.5 rounded mx-1">DEMO_MODE=true</code>
            in <code className="bg-amber-100 px-1.5 py-0.5 rounded">backend/.env</code> and restart the backend.</p>
          <p className="mt-2 text-xs">This is intentional — these endpoints should never be reachable in production.</p>
        </div>
      </div>
    )
  }

  const Section = ({ icon: Icon, title, desc, danger, children }) => (
    <div className={`bg-white rounded-xl border p-5 ${danger ? 'border-red-200' : 'border-gray-200'}`}>
      <div className="flex items-start gap-3 mb-3">
        <div className={`p-2 rounded-lg ${danger ? 'bg-red-50 text-red-600' : 'bg-blue-50 text-blue-600'}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-gray-800">{title}</h3>
          <p className="text-xs text-gray-500 mt-0.5">{desc}</p>
        </div>
      </div>
      {children}
    </div>
  )

  const ConfirmButton = ({ actionKey, label, dangerLabel, onConfirm, disabled, color = 'red' }) => {
    const isConfirming = confirming === actionKey
    const isBusy = busy[actionKey]
    const colors = {
      red: 'bg-red-600 hover:bg-red-700',
      amber: 'bg-amber-600 hover:bg-amber-700',
      gray: 'bg-gray-700 hover:bg-gray-800',
    }
    if (isConfirming) {
      return (
        <div className="flex gap-2">
          <button
            onClick={onConfirm}
            disabled={isBusy}
            className={`px-4 py-2 text-sm font-semibold text-white rounded-lg ${colors[color]} disabled:opacity-50`}
          >
            {isBusy ? 'Resetting…' : `Yes — ${dangerLabel}`}
          </button>
          <button
            onClick={() => setConfirming(null)}
            disabled={isBusy}
            className="px-4 py-2 text-sm border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50"
          >
            Cancel
          </button>
        </div>
      )
    }
    return (
      <button
        onClick={() => setConfirming(actionKey)}
        disabled={disabled}
        className={`px-4 py-2 text-sm font-medium text-white rounded-lg ${colors[color]} disabled:opacity-50 disabled:cursor-not-allowed`}
      >
        {label}
      </button>
    )
  }

  return (
    <div className="p-6 max-w-3xl space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
          <RotateCcw className="w-6 h-6 text-blue-600" />
          Demo / Testing
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Reset granular pieces of state so you can re-run the project lifecycle end-to-end during testing.
          All actions are logged and Admin-only.
        </p>
      </div>

      {msg && (
        <div className={`px-4 py-3 rounded-lg text-sm border ${msg.type === 'success' ? 'bg-green-50 border-green-200 text-green-700' : 'bg-red-50 border-red-200 text-red-700'}`} onClick={() => setMsg(null)}>
          {msg.text}
        </div>
      )}

      {/* ── 1. Reset onboarding ── */}
      <Section
        icon={RotateCcw}
        title={t('t_reset_onboarding')}
        desc="Clears checklist + identity + banking + consents. Status → Applied. Lets you re-run all 5 onboarding steps as that consultant."
      >
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-48">
            <label className="block text-xs text-gray-600 mb-1">{t('reset_consultant')}</label>
            <select
              value={targets.user_id}
              onChange={e => setTargets({ ...targets, user_id: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            >
              <option value="">— select a consultant —</option>
              {consultants.map(c => (
                <option key={c.id} value={c.id}>{c.full_name} ({c.email})</option>
              ))}
            </select>
          </div>
          <ConfirmButton
            actionKey="onb"
            label="Reset Onboarding"
            dangerLabel="Reset"
            color="amber"
            disabled={!targets.user_id}
            onConfirm={() => run('onb', () => api.post(`/demo/reset/onboarding/${targets.user_id}`))}
          />
        </div>
      </Section>

      {/* ── 2. Reset project ── */}
      <Section
        icon={Trash2}
        title={t('t_reset_project')}
        desc="Deletes the project's contracts, deliverables, tasks, invoices, milestones' completion, activity log. Keeps the project + its assigned consultant."
        danger
      >
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-48">
            <label className="block text-xs text-gray-600 mb-1">{t('reset_project')}</label>
            <select
              value={targets.project_id}
              onChange={e => setTargets({ ...targets, project_id: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            >
              <option value="">— select a project —</option>
              {projects.map(p => (
                <option key={p.id} value={p.id}>{p.name} (#{p.id}) — {p.status}</option>
              ))}
            </select>
          </div>
          <ConfirmButton
            actionKey="prj"
            label="Reset Project"
            dangerLabel="Reset & delete cascades"
            color="red"
            disabled={!targets.project_id}
            onConfirm={() => run('prj', () => api.post(`/demo/reset/project/${targets.project_id}`))}
          />
        </div>
      </Section>

      {/* ── 3. Reset contract ── */}
      <Section
        icon={RotateCcw}
        title={t('t_reset_contract')}
        desc="Clears signatures, generated doc, banking. Lets you re-test the whole sign workflow."
      >
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-48">
            <label className="block text-xs text-gray-600 mb-1">{t('reset_contract')}</label>
            <select
              value={targets.contract_id}
              onChange={e => setTargets({ ...targets, contract_id: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            >
              <option value="">— select a contract —</option>
              {contracts.map(c => (
                <option key={c.id} value={c.id}>{c.contract_number} — {c.status}</option>
              ))}
            </select>
          </div>
          <ConfirmButton
            actionKey="ctr"
            label="Reset Contract"
            dangerLabel="Reset"
            color="amber"
            disabled={!targets.contract_id}
            onConfirm={() => run('ctr', () => api.post(`/demo/reset/contract/${targets.contract_id}`))}
          />
        </div>
      </Section>

      {/* ── 4. Clear notifications ── */}
      <Section
        icon={Bell}
        title={t('t_clear_notifications')}
        desc="Wipes the in-app notification bell for every user. Doesn't touch any other data."
      >
        <ConfirmButton
          actionKey="notif"
          label="Clear Notifications"
          dangerLabel="Clear all"
          color="gray"
          onConfirm={() => run('notif', () => api.post('/demo/reset/notifications'))}
        />
      </Section>

      {/* ── 5. NUCLEAR: full reset ── */}
      <Section
        icon={Zap}
        title={t('t_full_reset')}
        desc="Wipes ALL projects, contracts, deliverables, invoices, disputes, onboarding state. Users remain. Use only when you want a blank slate."
        danger
      >
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-3 text-xs text-red-700 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{t('irreversible')} <code>python seed_full_demo.py</code> in the backend folder to repopulate demo data.</span>
        </div>
        <ConfirmButton
          actionKey="full"
          label="Full Reset"
          dangerLabel="WIPE EVERYTHING"
          color="red"
          onConfirm={() => run('full', () => api.post('/demo/reset/full'))}
        />
      </Section>
    </div>
  )
}
